import { collection, doc, addDoc, updateDoc, deleteDoc, getDocs, getDoc, query, orderBy, where, onSnapshot, increment, arrayUnion, arrayRemove } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './config';
import { normalizeFirebaseStorageUrl, normalizeStringArray } from './storageUrls';
import { Timestamp } from 'firebase/firestore';

const SQL_EXPORT_COLLECTIONS = [
  'profiles',
  'posts',
  'events',
  'notifications',
  'badgeTemplates',
  'stores',
  'products',
  'activityLogs',
  'siteAnnouncements',
  'userBans',
  'userWarnings',
  'flaggedContent',
  'supportRequests',
  'giveawayEntries',
  'giveawayWinners'
] as const;

export const ACTIVE_GIVEAWAY_ID = '2026-04-27-home-giveaway';
export const ACTIVE_GIVEAWAY_DRAW_DATE = '2026-04-27';

export interface FirebaseBackupPayload {
  exportedAt: string;
  version: number;
  source: 'firestore-client-export';
  collections: Record<string, Array<Record<string, any>>>;
  subcollections?: Record<string, Record<string, Array<Record<string, any>>>>;
}

const normalizeSqlIdentifier = (value: string): string =>
  value
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '') || 'field';

const toSqlValue = (value: any): string => {
  if (value === null || value === undefined) return 'NULL';
  if (value instanceof Date) return `'${value.toISOString().replace(/'/g, "''")}'`;
  if (value instanceof Timestamp) return `'${value.toDate().toISOString().replace(/'/g, "''")}'`;
  if (typeof value === 'object' && value && typeof value.toDate === 'function') {
    const date = value.toDate();
    if (date instanceof Date) {
      return `'${date.toISOString().replace(/'/g, "''")}'`;
    }
  }
  if (typeof value === 'number') return Number.isFinite(value) ? `'${String(value)}'` : 'NULL';
  if (typeof value === 'boolean') return `'${value ? 'true' : 'false'}'`;
  if (typeof value === 'string') return `'${value.replace(/'/g, "''")}'`;

  return `'${JSON.stringify(value).replace(/'/g, "''")}'`;
};

export const exportFirestoreAsSql = async (): Promise<string> => {
  const now = new Date().toISOString();
  const lines: string[] = [
    '-- Firestore SQL export',
    `-- Generated at ${now}`,
    '-- Database dialect: PostgreSQL-compatible',
    ''
  ];

  for (const collectionName of SQL_EXPORT_COLLECTIONS) {
    try {
      const snapshot = await getDocs(collection(db, collectionName));
      const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      if (docs.length === 0) {
        lines.push(`-- ${collectionName}: no rows`);
        lines.push('');
        continue;
      }

      const allKeys = new Set<string>(['id']);
      docs.forEach((row) => Object.keys(row).forEach((key) => allKeys.add(key)));
      const orderedKeys = Array.from(allKeys);
      const columnMap = new Map<string, string>();
      orderedKeys.forEach((key) => {
        columnMap.set(key, normalizeSqlIdentifier(key));
      });

      const tableName = normalizeSqlIdentifier(collectionName);
      const quotedColumns = orderedKeys.map((key) => `"${columnMap.get(key)}"`).join(', ');
      lines.push(`-- ${collectionName}: ${docs.length} rows`);
      lines.push(`CREATE TABLE IF NOT EXISTS "${tableName}" (${quotedColumns ? `\n  ${orderedKeys.map((key) => `"${columnMap.get(key)}" TEXT`).join(',\n  ')}\n` : ''});`);

      docs.forEach((row) => {
        const values = orderedKeys.map((key) => toSqlValue((row as Record<string, any>)[key])).join(', ');
        lines.push(`INSERT INTO "${tableName}" (${quotedColumns}) VALUES (${values});`);
      });
      lines.push('');
    } catch (error) {
      console.error(`Failed to export collection ${collectionName}:`, error);
      lines.push(`-- ${collectionName}: export failed`);
      lines.push('');
    }
  }

  return lines.join('\n');
};

export const exportFirestoreAsJsonBackup = async (): Promise<FirebaseBackupPayload> => {
  const backup: FirebaseBackupPayload = {
    exportedAt: new Date().toISOString(),
    version: 1,
    source: 'firestore-client-export',
    collections: {},
    subcollections: {}
  };

  for (const collectionName of SQL_EXPORT_COLLECTIONS) {
    try {
      const snapshot = await getDocs(collection(db, collectionName));
      backup.collections[collectionName] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error(`Failed to export collection ${collectionName}:`, error);
      backup.collections[collectionName] = [];
    }
  }

  // Include nested post comments because they are stored in subcollections.
  try {
    const posts = backup.collections.posts || [];
    const postComments: Record<string, Array<Record<string, any>>> = {};

    for (const post of posts) {
      const postId = post.id as string | undefined;
      if (!postId) continue;
      const commentsSnap = await getDocs(collection(db, 'posts', postId, 'comments'));
      postComments[postId] = commentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
    }

    backup.subcollections = {
      ...(backup.subcollections || {}),
      posts_comments: postComments
    };
  } catch (error) {
    console.error('Failed to export posts comments subcollections:', error);
  }

  return backup;
};

// Helper function to convert Firestore Timestamp to Date
export const convertTimestampToDate = (timestamp: Date | Timestamp): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  return new Date();
};

const normalizeUserProfileMedia = (profile: UserProfile): UserProfile => ({
  ...profile,
  profileImageUrl: normalizeFirebaseStorageUrl(profile.profileImageUrl),
  coverImageUrl: normalizeFirebaseStorageUrl(profile.coverImageUrl),
  photoGallery: normalizeStringArray(profile.photoGallery),
  achievements: profile.achievements?.map((achievement) => ({
    ...achievement,
    badgeImageUrl: normalizeFirebaseStorageUrl(achievement.badgeImageUrl)
  }))
});

const normalizePostMedia = (post: Post): Post => ({
  ...post,
  authorImageUrl: normalizeFirebaseStorageUrl(post.authorImageUrl),
  images: normalizeStringArray(post.images)
});

const normalizeEventMedia = (event: Event): Event => ({
  ...event,
  organizerImageUrl: normalizeFirebaseStorageUrl(event.organizerImageUrl || undefined) || null,
  imageUrl: normalizeFirebaseStorageUrl(event.imageUrl || undefined) || null
});

// User Profile Types
export interface UserProfile {
  id: string;
  uid: string;
  displayName: string;
  email: string;
  handle?: string;
  bio?: string;
  profileImageUrl?: string;
  coverImageUrl?: string;
  coverImagePosition?: number;
  photoGallery?: string[];
  culturalHeritage?: string;
  culturalLineage?: 'earth' | 'water' | 'air' | 'fire';
  clan?: string;
  tribe?: string;
  kinshipGroup?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  shareLocation?: boolean;
  interests?: string[];
  role?: 'user' | 'admin' | 'moderator' | 'developer';
  isBanned?: boolean;
  banReason?: string;
  bannedAt?: Date | Timestamp;
  bannedBy?: string;
  socialLinks?: {
    website?: string;
    instagram?: string;
    twitter?: string;
    facebook?: string;
    youtube?: string;
    tiktok?: string;
    discord?: string;
    other?: string;
  };
  lastSeen?: Date | Timestamp;
  isActive?: boolean;
  tosAcceptedAt?: Date | Timestamp;
  tosVersion?: string;
  locationConsent?: boolean;
  skills?: string[];
  featured?: string;
  achievements?: UserAchievement[];
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

export interface UserAchievement {
  badgeId: string;
  badgeName: string;
  badgeImageUrl?: string;
  badgeDescription?: string;
  templateId?: string;
  courseId?: string;
  courseName?: string;
  awardedBy?: string;
  earnedAt: Date | Timestamp;
  displayed?: boolean;
}

// Badge Template Types
export interface BadgeTemplate {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  category?: string;
  createdBy: string;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

// Post Types
export interface Post {
  id: string;
  authorId: string;
  authorName: string;
  authorHandle?: string;
  authorImageUrl?: string;
  title: string;
  content: string;
  images?: string[];
  category: 'cultural' | 'community' | 'learning' | 'events' | 'marketplace' | 'introduction';
  tags?: string[];
  kinshipGroups?: string[];
  likes: number;
  likedBy?: string[]; // user IDs who liked this post
  comments: number;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

// Comment on a post
export interface PostComment {
  id: string;
  postId: string;
  authorId: string;
  authorName: string;
  authorHandle?: string;
  authorImageUrl?: string;
  content: string;
  createdAt: Date | Timestamp;
}

// Stored notification (for post like/comment etc.)
export interface AppNotification {
  id: string;
  userId: string;
  type: 'post_like' | 'post_comment' | 'friend_request' | 'message' | 'mention' | 'info';
  title: string;
  message: string;
  read: boolean;
  createdAt: Date | Timestamp;
  metadata?: { postId?: string; fromUserId?: string; fromUserName?: string; url?: string };
}

// Message Types
export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  timestamp: Date | Timestamp;
  read: boolean;
}

// Event Types
export interface Event {
  id: string;
  title: string;
  description: string;
  location?: string | null;
  virtualLink?: string | null;
  eventType: 'in-person' | 'virtual' | 'hybrid';
  startDate: Date | Timestamp;
  endDate: Date | Timestamp;
  maxAttendees?: number | null;
  currentAttendees: string[];
  organizerId: string;
  organizerName: string;
  organizerHandle?: string | null;
  organizerImageUrl?: string | null;
  imageUrl?: string | null;
  category: 'cultural' | 'learning' | 'social' | 'spiritual' | 'community' | 'other';
  tags?: string[];
  isActive: boolean;
  isPublic: boolean;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

// Community Space Types
export interface CommunitySpace {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  members: string[];
  moderators: string[];
  category: string;
  createdAt: Date | Timestamp;
}

// User Profile Functions
export const createUserProfile = async (profile: Omit<UserProfile, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'profiles'), {
    ...profile,
    createdAt: new Date(),
    updatedAt: new Date()
  });
  return docRef.id;
};

export const updateUserProfile = async (profileId: string, updates: Partial<UserProfile>): Promise<void> => {
  const profileRef = doc(db, 'profiles', profileId);
  await updateDoc(profileRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const getUserProfile = async (profileId: string): Promise<UserProfile | null> => {
  const profileRef = doc(db, 'profiles', profileId);
  const profileSnap = await getDoc(profileRef);
  
  if (profileSnap.exists()) {
    return normalizeUserProfileMedia({ id: profileSnap.id, ...profileSnap.data() } as UserProfile);
  }
  return null;
};

export const getUserProfileByUid = async (uid: string): Promise<UserProfile | null> => {
  const q = query(collection(db, 'profiles'), where('uid', '==', uid));
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    const doc = querySnapshot.docs[0];
    return normalizeUserProfileMedia({ id: doc.id, ...doc.data() } as UserProfile);
  }
  return null;
};

export const getUserProfileByHandle = async (handle: string): Promise<UserProfile | null> => {
  const normalized = handle.replace(/^@+/, '').trim();
  if (!normalized) return null;

  const tryLookup = async (value: string) => {
    const q = query(collection(db, 'profiles'), where('handle', '==', value));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
      const doc = querySnapshot.docs[0];
      return normalizeUserProfileMedia({ id: doc.id, ...doc.data() } as UserProfile);
    }
    return null;
  };

  const candidates = Array.from(new Set([
    handle.trim(),
    normalized,
    `@${normalized}`,
    `@@${normalized}`
  ])).filter(Boolean);

  for (const candidate of candidates) {
    const match = await tryLookup(candidate);
    if (match) return match;
  }

  return null;
};

// Extract unique @handles mentioned in a block of text
export const extractMentionedHandles = (content: string): string[] => {
  const matches = content.match(/@([a-zA-Z0-9_]{2,30})/g) || [];
  return Array.from(new Set(matches.map((m) => m.slice(1))));
};

// Resolve @mentions in a post/comment and notify the mentioned users (skips the author).
const notifyMentionedUsers = async (
  content: string,
  authorId: string,
  authorName: string,
  postId: string
): Promise<void> => {
  const handles = extractMentionedHandles(content);
  if (handles.length === 0) return;

  await Promise.all(
    handles.map(async (handle) => {
      try {
        const mentionedProfile = await getUserProfileByHandle(handle);
        if (!mentionedProfile || mentionedProfile.uid === authorId) return;
        await createNotification({
          userId: mentionedProfile.uid,
          type: 'mention',
          title: 'You were tagged',
          message: `${authorName} tagged you in a post`,
          read: false,
          metadata: { postId, fromUserId: authorId, fromUserName: authorName, url: '/cultural-posts' }
        });
      } catch (e) {
        console.error(`Failed to notify mentioned user @${handle}:`, e);
      }
    })
  );
};

// Admin Functions
export const isAdmin = async (userId: string): Promise<boolean> => {
  try {
    const profile = await getUserProfileByUid(userId);
    return profile?.role === 'admin' || profile?.role === 'moderator';
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
};

export const isDeveloper = async (userId: string): Promise<boolean> => {
  try {
    const profile = await getUserProfileByUid(userId);
    return profile?.role === 'developer' || profile?.role === 'admin';
  } catch (error) {
    console.error('Error checking developer status:', error);
    return false;
  }
};

export const setUserRole = async (userId: string, role: 'user' | 'admin' | 'moderator' | 'developer'): Promise<void> => {
  const profileRef = doc(db, 'profiles', userId);
  await updateDoc(profileRef, {
    role,
    updatedAt: new Date()
  });
};

// Post Functions
export const createPost = async (post: Omit<Post, 'id' | 'createdAt' | 'updatedAt' | 'likes' | 'comments'>): Promise<string> => {
  // Remove undefined fields to prevent Firestore errors
  const cleanedPost = Object.entries(post).reduce((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key as keyof typeof post] = value;
    }
    return acc;
  }, {} as any);

  const docRef = await addDoc(collection(db, 'posts'), {
    ...cleanedPost,
    likes: 0,
    comments: 0,
    createdAt: new Date(),
    updatedAt: new Date()
  });

  if (post.content && post.authorId) {
    notifyMentionedUsers(post.content, post.authorId, post.authorName || 'Someone', docRef.id).catch((e) =>
      console.error('Failed to process post mentions:', e)
    );
  }

  return docRef.id;
};

export const getPosts = async (category?: string): Promise<Post[]> => {
  let q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
  
  if (category) {
    q = query(collection(db, 'posts'), where('category', '==', category), orderBy('createdAt', 'desc'));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => normalizePostMedia({ id: doc.id, ...doc.data() } as Post));
};

export const getPostById = async (postId: string): Promise<Post | null> => {
  const postRef = doc(db, 'posts', postId);
  const snap = await getDoc(postRef);
  if (!snap.exists()) return null;
  return normalizePostMedia({ id: snap.id, ...snap.data() } as Post);
};

// Real-time subscription for posts
export const subscribeToPosts = (callback: (posts: Post[]) => void): (() => void) => {
  const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, 
    (querySnapshot) => {
      const posts = querySnapshot.docs.map(doc => normalizePostMedia({ id: doc.id, ...doc.data() } as Post));
      callback(posts);
    },
    (error) => {
      console.error('Error in subscribeToPosts:', error);
      // Still call callback with empty array on error to prevent UI from hanging
      callback([]);
    }
  );
};

export const updatePost = async (postId: string, updates: Partial<Post>): Promise<void> => {
  const postRef = doc(db, 'posts', postId);
  await updateDoc(postRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deletePost = async (postId: string): Promise<void> => {
  await deleteDoc(doc(db, 'posts', postId));
};

/** Toggle like on a post. Returns true if post is now liked by the user, false if unliked. */
export const togglePostLike = async (postId: string, userId: string): Promise<boolean> => {
  const postRef = doc(db, 'posts', postId);
  const snap = await getDoc(postRef);
  if (!snap.exists()) return false;
  const data = snap.data();
  const authorId = data.authorId as string | undefined;
  const likedBy: string[] = data.likedBy || [];
  const isLiked = likedBy.includes(userId);
  if (isLiked) {
    await updateDoc(postRef, {
      likedBy: arrayRemove(userId),
      updatedAt: new Date()
    });
    return false;
  } else {
    await updateDoc(postRef, {
      likedBy: arrayUnion(userId),
      updatedAt: new Date()
    });
    // Notify post author (if not self)
    if (authorId && authorId !== userId) {
      try {
        const likerProfile = await getUserProfileByUid(userId);
        const fromName = likerProfile?.displayName || likerProfile?.handle || 'Someone';
        await createNotification({
          userId: authorId,
          type: 'post_like',
          title: 'New like',
          message: `${fromName} liked your post`,
          read: false,
          metadata: { postId, fromUserId: userId, url: '/cultural-posts' }
        });
      } catch (e) {
        console.error('Failed to create like notification:', e);
      }
    }
    return true;
  }
};

// Post comments (subcollection: posts/{postId}/comments)
export const addComment = async (
  postId: string,
  comment: Omit<PostComment, 'id' | 'postId' | 'createdAt'>
): Promise<string> => {
  const postRef = doc(db, 'posts', postId);
  const postSnap = await getDoc(postRef);
  const postAuthorId = postSnap.exists() ? (postSnap.data().authorId as string | undefined) : undefined;

  const commentsRef = collection(db, 'posts', postId, 'comments');
  const docRef = await addDoc(commentsRef, {
    ...comment,
    postId,
    createdAt: new Date()
  });
  await updateDoc(postRef, {
    comments: increment(1),
    updatedAt: new Date()
  });

  // Notify post author (if not self)
  if (postAuthorId && postAuthorId !== comment.authorId) {
    try {
      await createNotification({
        userId: postAuthorId,
        type: 'post_comment',
        title: 'New comment',
        message: `${comment.authorName} commented on your post`,
        read: false,
        metadata: { postId, fromUserId: comment.authorId, fromUserName: comment.authorName, url: '/cultural-posts' }
      });
    } catch (e) {
      console.error('Failed to create comment notification:', e);
    }
  }

  notifyMentionedUsers(comment.content, comment.authorId, comment.authorName || 'Someone', postId).catch((e) =>
    console.error('Failed to process comment mentions:', e)
  );

  return docRef.id;
};

export const subscribeToCommentsForPost = (
  postId: string,
  callback: (comments: PostComment[]) => void
): (() => void) => {
  const commentsRef = collection(db, 'posts', postId, 'comments');
  const q = query(commentsRef, orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const comments = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data()
      })) as PostComment[];
      callback(comments);
    },
    (error) => {
      console.error('Error subscribing to comments:', error);
      callback([]);
    }
  );
};

// Notifications (for post like/comment)
export const createNotification = async (
  notification: Omit<AppNotification, 'id' | 'createdAt'>
): Promise<string> => {
  const ref = await addDoc(collection(db, 'notifications'), {
    ...notification,
    createdAt: new Date()
  });
  return ref.id;
};

export const subscribeToNotifications = (
  userId: string,
  callback: (notifications: AppNotification[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'notifications'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const list = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data()
      })) as AppNotification[];
      callback(list);
    },
    (error) => {
      console.error('Error subscribing to notifications:', error);
      callback([]);
    }
  );
};

export const markNotificationRead = async (notificationId: string): Promise<void> => {
  await updateDoc(doc(db, 'notifications', notificationId), { read: true });
};

// Alias for createPost
export const addPost = createPost;

// Event Functions
export const createEvent = async (event: Omit<Event, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'events'), {
    ...event,
    createdAt: new Date(),
    updatedAt: new Date()
  });
  return docRef.id;
};

export const updateEvent = async (eventId: string, updates: Partial<Event>): Promise<void> => {
  const eventRef = doc(db, 'events', eventId);
  await updateDoc(eventRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deleteEvent = async (eventId: string): Promise<void> => {
  await deleteDoc(doc(db, 'events', eventId));
};

export const getEvents = async (category?: string): Promise<Event[]> => {
  let q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
  
  if (category) {
    q = query(collection(db, 'events'), where('category', '==', category), orderBy('createdAt', 'desc'));
  }
  
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => normalizeEventMedia({ id: doc.id, ...doc.data() } as Event));
};

// Real-time subscription for events
export const subscribeToEvents = (callback: (events: Event[]) => void): (() => void) => {
  const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
  
  return onSnapshot(q, 
    (querySnapshot) => {
      const events = querySnapshot.docs.map(doc => normalizeEventMedia({ id: doc.id, ...doc.data() } as Event));
      callback(events);
    },
    (error) => {
      console.error('Error in subscribeToEvents:', error);
      callback([]);
    }
  );
};

// Learning courses (for global feed)
export interface LearningCourseFeedItem {
  id: string;
  title: string;
  description?: string;
  createdAt: Date | Timestamp;
}

export const subscribeToLearningCourses = (
  callback: (courses: LearningCourseFeedItem[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'learningCourses'),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snapshot) => {
      const list = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data()
      })) as LearningCourseFeedItem[];
      callback(list);
    },
    (error) => {
      console.error('Error in subscribeToLearningCourses:', error);
      callback([]);
    }
  );
};

export const getEventById = async (eventId: string): Promise<Event | null> => {
  const eventRef = doc(db, 'events', eventId);
  const eventSnap = await getDoc(eventRef);
  
  if (eventSnap.exists()) {
    return normalizeEventMedia({ id: eventSnap.id, ...eventSnap.data() } as Event);
  }
  return null;
};

export const joinEvent = async (eventId: string, userId: string): Promise<void> => {
  const eventRef = doc(db, 'events', eventId);
  const eventSnap = await getDoc(eventRef);
  
  if (eventSnap.exists()) {
    const event = eventSnap.data() as Event;
    if (!event.currentAttendees.includes(userId)) {
      const updatedAttendees = [...event.currentAttendees, userId];
      await updateDoc(eventRef, { currentAttendees: updatedAttendees });
    }
  }
};

export const leaveEvent = async (eventId: string, userId: string): Promise<void> => {
  const eventRef = doc(db, 'events', eventId);
  const eventSnap = await getDoc(eventRef);
  
  if (eventSnap.exists()) {
    const event = eventSnap.data() as Event;
    const updatedAttendees = event.currentAttendees.filter(id => id !== userId);
    await updateDoc(eventRef, { currentAttendees: updatedAttendees });
  }
};

// Image Upload Functions
export const uploadImage = async (file: File, path: string): Promise<string> => {
  try {
    console.log('Starting image upload...');
    const imageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
    console.log('Uploading to path:', imageRef.fullPath);
    
    const snapshot = await uploadBytes(imageRef, file);
    console.log('Upload completed, getting download URL...');
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Download URL obtained:', downloadURL);
    
    return normalizeFirebaseStorageUrl(downloadURL) || downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw new Error(`Failed to upload image: ${error}`);
  }
};

// Community Space Functions
export const createCommunitySpace = async (space: Omit<CommunitySpace, 'id' | 'createdAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'communitySpaces'), {
    ...space,
    createdAt: new Date()
  });
  return docRef.id;
};

export const getCommunitySpaces = async (): Promise<CommunitySpace[]> => {
  const querySnapshot = await getDocs(collection(db, 'communitySpaces'));
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as CommunitySpace));
};

export const joinCommunitySpace = async (spaceId: string, userId: string): Promise<void> => {
  const spaceRef = doc(db, 'communitySpaces', spaceId);
  const spaceSnap = await getDoc(spaceRef);
  
  if (spaceSnap.exists()) {
    const space = spaceSnap.data() as CommunitySpace;
    const updatedMembers = [...space.members, userId];
    await updateDoc(spaceRef, { members: updatedMembers });
  }
};

// Message Functions
export const sendMessage = async (message: Omit<Message, 'id' | 'timestamp'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'messages'), {
    ...message,
    timestamp: new Date()
  });
  return docRef.id;
};

export const getMessages = async (userId: string): Promise<Message[]> => {
  const q = query(
    collection(db, 'messages'),
    where('receiverId', '==', userId),
    orderBy('timestamp', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Message));
};

// Search for all users by name or handle
export const searchUsers = async (searchTerm: string, currentUserId: string): Promise<UserProfile[]> => {
  const profilesRef = collection(db, 'profiles');
  const querySnapshot = await getDocs(profilesRef);
  
  const allUsers = querySnapshot.docs.map(doc => normalizeUserProfileMedia({ 
    id: doc.id, 
    ...doc.data() 
  } as UserProfile));
  
  // Filter out current user
  const filteredUsers = allUsers.filter(user => user.uid !== currentUserId);
  
  // If no search term, return all users (excluding current user)
  if (!searchTerm.trim()) {
    return filteredUsers;
  }
  
  // Filter by name or handle
  return filteredUsers.filter(user => 
    user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.handle && user.handle.toLowerCase().includes(searchTerm.toLowerCase()))
  );
};

// Real-time subscription for users/profiles
export const subscribeToUsers = (callback: (users: UserProfile[]) => void): (() => void) => {
  const profilesRef = collection(db, 'profiles');
  
  return onSnapshot(profilesRef, 
    (querySnapshot) => {
      const users = querySnapshot.docs.map(doc => normalizeUserProfileMedia({ 
        id: doc.id, 
        ...doc.data() 
      } as UserProfile));
      callback(users);
    },
    (error) => {
      console.error('Error in subscribeToUsers:', error);
      callback([]);
    }
  );
};

// Comprehensive search across all content types
export interface SearchResult {
  type: 'user' | 'post' | 'event' | 'chatroom';
  id: string;
  title: string;
  description?: string;
  imageUrl?: string;
  url: string;
  metadata?: any;
}

export const searchEverything = async (searchTerm: string, currentUserId: string): Promise<SearchResult[]> => {
  if (!searchTerm.trim() || searchTerm.length < 2) {
    return [];
  }

  const term = searchTerm.toLowerCase();
  const results: SearchResult[] = [];

  try {
    // Search Users
    const usersQuery = collection(db, 'profiles');
    const usersSnapshot = await getDocs(usersQuery);
    usersSnapshot.docs.forEach(doc => {
      const user = normalizeUserProfileMedia({ id: doc.id, ...doc.data() } as UserProfile);
      if (user.uid !== currentUserId) {
        const matchesName = user.displayName?.toLowerCase().includes(term);
        const matchesHandle = user.handle?.toLowerCase().includes(term);
        if (matchesName || matchesHandle) {
          results.push({
            type: 'user',
            id: user.uid,
            title: user.displayName || 'Unknown User',
            description: user.handle || user.email,
            imageUrl: normalizeFirebaseStorageUrl(user.profileImageUrl),
            url: `/profile/${user.uid}`,
            metadata: { handle: user.handle }
          });
        }
      }
    });

    // Search Posts
    const postsQuery = collection(db, 'posts');
    const postsSnapshot = await getDocs(postsQuery);
    postsSnapshot.docs.forEach(doc => {
      const post = normalizePostMedia({ id: doc.id, ...doc.data() } as Post);
      const matchesTitle = post.title?.toLowerCase().includes(term);
      const matchesContent = post.content?.toLowerCase().includes(term);
      if (matchesTitle || matchesContent) {
        results.push({
          type: 'post',
          id: post.id,
          title: post.title || 'Untitled Post',
          description: post.content?.substring(0, 100) + (post.content && post.content.length > 100 ? '...' : ''),
          imageUrl: normalizeFirebaseStorageUrl(post.images?.[0]),
          url: `/cultural-posts`,
          metadata: { category: post.category, authorName: post.authorName }
        });
      }
    });

    // Search Events
    const eventsQuery = collection(db, 'events');
    const eventsSnapshot = await getDocs(eventsQuery);
    eventsSnapshot.docs.forEach(doc => {
      const event = normalizeEventMedia({ id: doc.id, ...doc.data() } as Event);
      const matchesTitle = event.title?.toLowerCase().includes(term);
      const matchesDescription = event.description?.toLowerCase().includes(term);
      if (matchesTitle || matchesDescription) {
        results.push({
          type: 'event',
          id: event.id,
          title: event.title || 'Untitled Event',
          description: event.description?.substring(0, 100) + (event.description && event.description.length > 100 ? '...' : ''),
          imageUrl: normalizeFirebaseStorageUrl(event.imageUrl || undefined),
          url: `/events`,
          metadata: { 
            category: event.category,
            startDate: event.startDate,
            eventType: event.eventType
          }
        });
      }
    });

    // Search Chat Rooms (Community Spaces)
    const chatRoomsQuery = collection(db, 'chatRooms');
    const chatRoomsSnapshot = await getDocs(chatRoomsQuery);
    chatRoomsSnapshot.docs.forEach(doc => {
      const room = { id: doc.id, ...doc.data() } as any;
      const matchesName = room.name?.toLowerCase().includes(term);
      const matchesDescription = room.description?.toLowerCase().includes(term);
      if (matchesName || matchesDescription) {
        results.push({
          type: 'chatroom',
          id: room.id,
          title: room.name || 'Untitled Topic',
          description: room.description?.substring(0, 100) + (room.description && room.description.length > 100 ? '...' : ''),
          imageUrl: normalizeFirebaseStorageUrl(room.bannerImageUrl || room.logoImageUrl),
          url: `/community`,
          metadata: { category: room.category, memberCount: room.memberCount }
        });
      }
    });
  } catch (error) {
    console.error('Error in comprehensive search:', error);
  }

  // Sort by relevance (title matches first, then description matches)
  return results.sort((a, b) => {
    const aTitleMatch = a.title.toLowerCase().includes(term);
    const bTitleMatch = b.title.toLowerCase().includes(term);
    if (aTitleMatch && !bTitleMatch) return -1;
    if (!aTitleMatch && bTitleMatch) return 1;
    return 0;
  }).slice(0, 10); // Limit to 10 results total
};

// Achievement Functions
export const awardAchievement = async (
  userId: string,
  badgeId: string,
  badgeName: string,
  courseId: string,
  courseName: string,
  badgeImageUrl?: string,
  extra?: Partial<UserAchievement>
): Promise<void> => {
  const profile = await getUserProfileByUid(userId);
  if (!profile) {
    throw new Error('User profile not found');
  }

  const currentAchievements: UserAchievement[] = profile.achievements || [];
  const alreadyHasAchievement = currentAchievements.some(
    (achievement) =>
      achievement.badgeId === badgeId ||
      (extra?.templateId != null && achievement.templateId === extra.templateId)
  );

  if (alreadyHasAchievement) {
    return;
  }

  const newAchievement: UserAchievement = {
    badgeId,
    badgeName,
    badgeImageUrl: badgeImageUrl || '',
    courseId,
    courseName,
    earnedAt: new Date(),
    displayed: true,
    ...extra,
  };

  await updateUserProfile(profile.id, {
    achievements: [...currentAchievements, newAchievement],
  });
};

export const awardBadgeFromTemplate = async (
  userId: string,
  template: BadgeTemplate,
  awardedBy?: string
): Promise<void> => {
  await awardAchievement(
    userId,
    template.id,
    template.name,
    'community',
    template.category || 'Community',
    template.imageUrl,
    {
      templateId: template.id,
      badgeDescription: template.description,
      awardedBy,
    }
  );
};

export const removeAchievement = async (
  userId: string,
  badgeId: string,
  courseId?: string
): Promise<void> => {
  const profile = await getUserProfileByUid(userId);
  if (!profile) {
    throw new Error('User profile not found');
  }

  const currentAchievements: UserAchievement[] = profile.achievements || [];
  const updatedAchievements = currentAchievements.filter((achievement) => {
    if (achievement.badgeId !== badgeId && achievement.templateId !== badgeId) {
      return true;
    }
    if (!courseId) {
      return false;
    }
    return achievement.courseId !== courseId;
  });

  await updateUserProfile(profile.id, {
    achievements: updatedAchievements,
  });
};

// Badge Template Functions
export const createBadgeTemplate = async (
  name: string,
  description: string,
  imageUrl: string,
  createdBy: string,
  category?: string
): Promise<string> => {
  const docRef = await addDoc(collection(db, 'badgeTemplates'), {
    name,
    description,
    imageUrl,
    category: category || 'general',
    createdBy,
    createdAt: new Date(),
    updatedAt: new Date()
  });
  return docRef.id;
};

export const getBadgeTemplates = async (): Promise<BadgeTemplate[]> => {
  const querySnapshot = await getDocs(query(collection(db, 'badgeTemplates'), orderBy('createdAt', 'desc')));
  return querySnapshot.docs.map(doc => {
    const badge = { id: doc.id, ...doc.data() } as BadgeTemplate;
    return {
      ...badge,
      imageUrl: normalizeFirebaseStorageUrl(badge.imageUrl) || badge.imageUrl
    };
  });
};

export const updateBadgeTemplate = async (
  templateId: string,
  updates: Partial<BadgeTemplate>
): Promise<void> => {
  const templateRef = doc(db, 'badgeTemplates', templateId);
  await updateDoc(templateRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deleteBadgeTemplate = async (templateId: string): Promise<void> => {
  await deleteDoc(doc(db, 'badgeTemplates', templateId));
};

// Store Types
export interface Store {
  id: string;
  name: string;
  description: string;
  logoUrl?: string;
  owner: string;
  isActive: boolean;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

// Store Functions
export const createStore = async (store: Omit<Store, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'stores'), {
    ...store,
    createdAt: new Date(),
    updatedAt: new Date()
  });
  return docRef.id;
};

export const getStores = async (): Promise<Store[]> => {
  const q = query(collection(db, 'stores'), where('isActive', '==', true), orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const store = { id: doc.id, ...doc.data() } as Store;
    return {
      ...store,
      logoUrl: normalizeFirebaseStorageUrl(store.logoUrl)
    };
  });
};

export const subscribeToStores = (callback: (stores: Store[]) => void): (() => void) => {
  const q = query(collection(db, 'stores'), where('isActive', '==', true), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snapshot) => {
    const stores = snapshot.docs.map(doc => {
      const store = { id: doc.id, ...doc.data() } as Store;
      return {
        ...store,
        logoUrl: normalizeFirebaseStorageUrl(store.logoUrl)
      };
    });
    callback(stores);
  });
};

export const updateStore = async (storeId: string, updates: Partial<Store>): Promise<void> => {
  const storeRef = doc(db, 'stores', storeId);
  await updateDoc(storeRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deleteStore = async (storeId: string): Promise<void> => {
  const storeRef = doc(db, 'stores', storeId);
  await updateDoc(storeRef, { isActive: false, updatedAt: new Date() });
};
// Product Types
export interface Product {
  id: string;
  storeId: string;
  name: string;
  description: string;
  price: number;
  imageUrl?: string;
  purchaseLink: string;
  isActive: boolean;
  createdAt: Date | Timestamp;
  updatedAt: Date | Timestamp;
}

export interface GiveawayEntry {
  id: string;
  giveawayId: string;
  userId: string;
  userName: string;
  userHandle?: string;
  userEmail?: string;
  enteredAt: Date | Timestamp;
}

export interface GiveawayWinner {
  id: string;
  giveawayId: string;
  userId: string;
  userName: string;
  userHandle?: string;
  userEmail?: string;
  drawnAt: Date | Timestamp;
  drawnByAdminId: string;
  drawnByAdminName: string;
  totalEntries: number;
}

// Product Functions
export const createProduct = async (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
  const docRef = await addDoc(collection(db, 'products'), {
    ...product,
    createdAt: new Date(),
    updatedAt: new Date()
  });
  return docRef.id;
};

export const getProductsByStore = async (storeId: string): Promise<Product[]> => {
  const q = query(
    collection(db, 'products'),
    where('storeId', '==', storeId),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => {
    const product = { id: doc.id, ...doc.data() } as Product;
    return {
      ...product,
      imageUrl: normalizeFirebaseStorageUrl(product.imageUrl)
    };
  });
};

export const subscribeToProducts = (storeId: string, callback: (products: Product[]) => void): (() => void) => {
  const q = query(
    collection(db, 'products'),
    where('storeId', '==', storeId),
    where('isActive', '==', true),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snapshot) => {
    const products = snapshot.docs.map(doc => {
      const product = { id: doc.id, ...doc.data() } as Product;
      return {
        ...product,
        imageUrl: normalizeFirebaseStorageUrl(product.imageUrl)
      };
    });
    callback(products);
  });
};

export const updateProduct = async (productId: string, updates: Partial<Product>): Promise<void> => {
  const productRef = doc(db, 'products', productId);
  await updateDoc(productRef, {
    ...updates,
    updatedAt: new Date()
  });
};

export const deleteProduct = async (productId: string): Promise<void> => {
  const productRef = doc(db, 'products', productId);
  await updateDoc(productRef, { isActive: false, updatedAt: new Date() });
};

export const hasUserJoinedGiveaway = async (
  userId: string,
  giveawayId: string = ACTIVE_GIVEAWAY_ID
): Promise<boolean> => {
  const q = query(
    collection(db, 'giveawayEntries'),
    where('giveawayId', '==', giveawayId),
    where('userId', '==', userId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

export const joinGiveaway = async (
  userId: string,
  userName: string,
  userHandle?: string,
  userEmail?: string,
  giveawayId: string = ACTIVE_GIVEAWAY_ID
): Promise<{ joined: boolean; alreadyJoined: boolean }> => {
  const alreadyJoined = await hasUserJoinedGiveaway(userId, giveawayId);
  if (alreadyJoined) {
    return { joined: false, alreadyJoined: true };
  }

  await addDoc(collection(db, 'giveawayEntries'), {
    giveawayId,
    userId,
    userName,
    userHandle: userHandle || '',
    userEmail: userEmail || '',
    enteredAt: new Date()
  });

  return { joined: true, alreadyJoined: false };
};

export const getGiveawayEntries = async (
  giveawayId: string = ACTIVE_GIVEAWAY_ID
): Promise<GiveawayEntry[]> => {
  try {
    const q = query(
      collection(db, 'giveawayEntries'),
      where('giveawayId', '==', giveawayId),
      orderBy('enteredAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as GiveawayEntry));
  } catch (error) {
    // Fallback if composite index is missing: fetch by giveawayId and sort client-side.
    const fallbackQuery = query(
      collection(db, 'giveawayEntries'),
      where('giveawayId', '==', giveawayId)
    );
    const snapshot = await getDocs(fallbackQuery);
    return snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() } as GiveawayEntry))
      .sort((a, b) => convertTimestampToDate(b.enteredAt).getTime() - convertTimestampToDate(a.enteredAt).getTime());
  }
};

export const getGiveawayWinners = async (
  giveawayId: string = ACTIVE_GIVEAWAY_ID
): Promise<GiveawayWinner[]> => {
  try {
    const q = query(
      collection(db, 'giveawayWinners'),
      where('giveawayId', '==', giveawayId),
      orderBy('drawnAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as GiveawayWinner));
  } catch (error) {
    // Fallback if composite index is missing: fetch by giveawayId and sort client-side.
    const fallbackQuery = query(
      collection(db, 'giveawayWinners'),
      where('giveawayId', '==', giveawayId)
    );
    const snapshot = await getDocs(fallbackQuery);
    return snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() } as GiveawayWinner))
      .sort((a, b) => convertTimestampToDate(b.drawnAt).getTime() - convertTimestampToDate(a.drawnAt).getTime());
  }
};

export const drawGiveawayWinner = async (
  adminId: string,
  adminName: string,
  giveawayId: string = ACTIVE_GIVEAWAY_ID
): Promise<GiveawayWinner> => {
  const entries = await getGiveawayEntries(giveawayId);
  if (entries.length === 0) {
    throw new Error('No giveaway entries to draw from.');
  }

  const randomIndex = Math.floor(Math.random() * entries.length);
  const winner = entries[randomIndex];

  const winnerDoc = {
    giveawayId,
    userId: winner.userId,
    userName: winner.userName,
    userHandle: winner.userHandle || '',
    userEmail: winner.userEmail || '',
    drawnAt: new Date(),
    drawnByAdminId: adminId,
    drawnByAdminName: adminName,
    totalEntries: entries.length
  };

  const ref = await addDoc(collection(db, 'giveawayWinners'), winnerDoc);
  return { id: ref.id, ...winnerDoc } as GiveawayWinner;
};
